import axios from 'axios';

// Installs a response interceptor on the default axios instance that retries
// requests rejected with 429 (Too Many Requests) using exponential backoff with
// jitter. The API gateway rate-limits the several parallel calls the training
// page fires on load; a short backoff smooths those bursts out transparently.

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 500;
const MAX_DELAY_MS = 5000;

// Prefer the server's Retry-After hint when present (seconds or HTTP date),
// otherwise fall back to exponential backoff (500ms, 1s, 2s...) plus jitter.
function retryDelay(attempt, response){
    const retryAfter = response && response.headers && response.headers['retry-after'];
    if (retryAfter){
        const seconds = Number(retryAfter);
        if (!Number.isNaN(seconds)){
            return seconds * 1000;
        }
        const dateMs = Date.parse(retryAfter);
        if (!Number.isNaN(dateMs)){
            return Math.max(0, dateMs - Date.now());
        }
    }
    const backoff = Math.min(BASE_DELAY_MS * (2 ** attempt), MAX_DELAY_MS);
    const jitter = Math.random() * BASE_DELAY_MS;
    return backoff + jitter;
}

export function installAxiosRetry(){
    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const config = error.config;
            const response = error.response;

            if (!config || !response || response.status !== 429){
                return Promise.reject(error);
            }

            config.__retryCount = config.__retryCount || 0;
            if (config.__retryCount >= MAX_RETRIES){
                return Promise.reject(error);
            }

            config.__retryCount += 1;
            const delay = retryDelay(config.__retryCount - 1, response);

            return new Promise((resolve) => setTimeout(resolve, delay))
                .then(() => axios(config));
        }
    );
}
